'use es6';

import { Range, List } from 'immutable';
import { getSortedChildren, distributeEventsToWeekDays, getNoUndefinedEvents, getCorrectOrderOfEventsWithMultiDay, getEventsTaggedWithHiddenMultiDayEvents } from './eventParse';
import { getDisplayMonthRows, getDisplayStartDate, add, getMomentMonth } from './time';
import { MAX_MULTI_DAY_EVENTS_PER_DAY } from '../constants/events';
import { getEventsToDisplay } from './eventDisplay';

/**
 * @deprecated This is a legacy method that uses events based on React component props (and not actual calendar events). This method is still being used for historical reasons.
 *
 * Whenever making updates to this method, make sure to also update `getEventMapForDay` in `ui-addon-calendars/static-2.4158/js/lib/eventList.ts`.
 */
export function getEventMapForDay({
  events,
  maxEventDisplayCount
}) {
  let hasOverflow = events.size > maxEventDisplayCount;
  const eventsToDisplay = events.reduce((accumulator, event, index) => {
    const isMultiDayEventHidden = event && event.multiDayEventHidden;
    if (isMultiDayEventHidden) {
      hasOverflow = true;
    }
    const shouldBeInOverflow = hasOverflow && index + 1 >= maxEventDisplayCount || isMultiDayEventHidden;
    if (!event || shouldBeInOverflow) {
      return accumulator;
    }
    return accumulator.push({
      event,
      displayIndex: index
    });
  }, List());
  const allNonPlaceholderEvents = events.filter(Boolean);
  const overflowCount = allNonPlaceholderEvents.size - eventsToDisplay.size;
  return {
    displayEvents: eventsToDisplay,
    events: allNonPlaceholderEvents,
    overflowCount,
    hasOverflow
  };
}

/**
 * @deprecated This is a legacy method that uses events based on React component props (and not actual calendar events). This method is still being used for historical reasons.
 *
 * Whenever making updates to this method, make sure to also update `getEventMapForWeek` in `ui-addon-calendars/static-2.4158/js/lib/eventList.ts`.
 */
export function getEventMapForWeek({
  week,
  children,
  displayYear,
  displayMonth,
  maxEventDisplayCount,
  timezone,
  hideWeekends
}) {
  const startCutoff = getDisplayStartDate(displayYear, displayMonth, timezone);
  const weekStartDayRaw = add(startCutoff, 7 * week, 'days');
  const weekStartDay = timezone ? weekStartDayRaw.tz(timezone).startOf('day') : weekStartDayRaw.startOf('day');
  const weekEndDayRaw = add(startCutoff, 7 * (week + 1) - 1, 'days');
  const weekEndDay = timezone ? weekEndDayRaw.tz(timezone).endOf('day') : weekEndDayRaw.endOf('day');
  const eventsRaw = distributeEventsToWeekDays(children, weekStartDay, weekEndDay, timezone, hideWeekends);
  const events = getNoUndefinedEvents(eventsRaw);
  const sortedEvents = getCorrectOrderOfEventsWithMultiDay({
    events,
    maxEventDisplayCount,
    maxMultiDayEventDisplayCount: MAX_MULTI_DAY_EVENTS_PER_DAY
  });
  const finalEvents = getEventsTaggedWithHiddenMultiDayEvents({
    events: sortedEvents,
    maxEventDisplayCount,
    maxMultiDayEventDisplayCount: MAX_MULTI_DAY_EVENTS_PER_DAY
  });
  const result = Range(0, 7).map(index => {
    return getEventMapForDay({
      events: finalEvents[index],
      maxEventDisplayCount
    });
  }).toList();
  return result;
}

/**
 * @deprecated This is a legacy method that uses events based on React component props (and not actual calendar events). This method is still being used for historical reasons.
 *
 * Whenever making updates to this method, make sure to also update `groupEventsByWeekRowAndDayOfWeek` in `ui-addon-calendars/static-2.4158/js/lib/eventList.ts`.
 */
export function getEventMapForMonth({
  children,
  displayYear,
  displayMonth,
  maxEventDisplayCount,
  timezone,
  eventDisplay,
  hideWeekends
}) {
  const momentMonth = getMomentMonth(displayYear, displayMonth);
  const start = momentMonth.clone().startOf('month').startOf('week');
  const end = momentMonth.clone().endOf('month').endOf('week');
  const sortedChildren = getSortedChildren(children, false, timezone);
  const eventsToDisplay = getEventsToDisplay({
    sortedChildren,
    eventDisplay,
    eventRange: {
      start,
      end
    }
  });
  const displayMonthRows = getDisplayMonthRows(displayYear, displayMonth);
  return Range(0, displayMonthRows).map(week => {
    return getEventMapForWeek({
      week,
      children: eventsToDisplay.allEvents,
      displayYear,
      displayMonth,
      maxEventDisplayCount,
      timezone,
      hideWeekends
    });
  }).toList();
}